import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchValorSensorTramaById, limpiarSensor } from '../actions/sensor';

/**
 * Hook personalizado para manejar datos de trama de sensor con gráficas
 * @param {string} sensorId - ID del sensor
 * @param {number} refreshInterval - Intervalo de actualización en ms (default: 30000)
 */
export const useSensorTrama = (sensorId, refreshInterval = 30000) => {
  const dispatch = useDispatch();
  const { sensorTrama, isLoading, error } = useSelector((state) => state.sensorReducer);
  
  const [estado, setEstado] = useState({
    loading: true,
    error: null,
    hasData: false
  });

  const cargarDatos = useCallback(async () => {
    if (!sensorId) return;
    
    try {
      setEstado(prev => ({ ...prev, loading: true, error: null }));
      await dispatch(fetchValorSensorTramaById(sensorId));
      setEstado(prev => ({ ...prev, loading: false, hasData: true }));
    } catch (error) {
      setEstado(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message || 'Error al cargar datos de trama del sensor',
        hasData: false 
      }));
    }
  }, [dispatch, sensorId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    if (!sensorId || !refreshInterval) return;

    const intervalId = setInterval(() => {
      dispatch(fetchValorSensorTramaById(sensorId));
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
      dispatch(limpiarSensor());
    };
  }, [dispatch, sensorId, refreshInterval]);

  // Verificar si tiene datos de gráfica
  const tieneGrafica = sensorTrama?.data && Array.isArray(sensorTrama.data) && sensorTrama.data.length > 0;
  
  // Preparar datos para la gráfica (formato compatible con CardGrafica)
  const datosGrafica = tieneGrafica ? [{
    ...sensorTrama,
    data: sensorTrama.data
  }] : [];

  // Estadísticas de los datos (ya vienen del backend)
  const estadisticas = sensorTrama?.rango ? {
    totalDatos: sensorTrama.totalPuntos || 0,
    ultimoValor: sensorTrama.ultimoValor,
    ultimaFecha: sensorTrama.ultimaFecha,
    valorMinimo: sensorTrama.rango.minimo,
    valorMaximo: sensorTrama.rango.maximo,
    promedio: sensorTrama.rango.promedio
  } : null;

  return {
    sensorTrama,
    isLoading: isLoading || estado.loading,
    error: error || estado.error,
    hasData: estado.hasData && !!sensorTrama,
    tieneGrafica,
    datosGrafica,
    estadisticas,
    refrescar: cargarDatos
  };
};

export default useSensorTrama;